import React, { useState, useEffect, useRef, useMemo } from "react";
import { X, RotateCw, FileText, Search, MoveDown, MoveUp, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker (recommended approach)
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


interface FilePreviewModalProps {
  fileUrl: string;
  fileName: string;
  onClose: () => void;
}

const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ fileUrl, fileName, onClose }) => {
  const [content, setContent] = useState<string | null>(null);
  const [excelData, setExcelData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // PDF specific states
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);
  
  const fileExtension = fileName.split('.').pop()?.toLowerCase();

  // Define file types
  const isWordFile = fileExtension === 'docx' || fileExtension === 'doc';
  const isExcelFile = fileExtension === 'xlsx' || fileExtension === 'xls';
  const isPdfFile = fileExtension === 'pdf';

  // PDF.js options - defined outside component to avoid re-renders
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`
  }), []);

  // PDF Document Load Success
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    setPdfError(null);
    setPdfLoading(false);
  };

  // PDF Document Load Start
  const onDocumentLoadStart = () => {
    setPdfLoading(true);
    setLoading(true);
    setPdfError(null);
  };

  // PDF Document Load Error
  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    setPdfError(`Failed to load PDF: ${error.message}`);
    setLoading(false);
    setPdfLoading(false);
  };

  // PDF Page Load Success
  const onPageLoadSuccess = () => {
    // Page loaded successfully
  };

  // PDF Page Load Error
  const onPageLoadError = (error: Error) => {
    console.error('PDF page load error:', error);
  };

  // PDF Page Navigation
  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const goToPage = (page: number) => setPageNumber(Math.max(1, Math.min(page, numPages)));

  // PDF Zoom Controls
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setScale(1.0);

  // Download PDF
  const downloadPdf = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isPdfFile || numPages === 0) return;
      
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          goToPrevPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          goToNextPage();
          break;
        case 'Home':
          event.preventDefault();
          setPageNumber(1);
          break;
        case 'End':
          event.preventDefault();
          setPageNumber(numPages);
          break;
        case '+':
        case '=':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            zoomIn();
          }
          break;
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            zoomOut();
          }
          break;
        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            resetZoom();
          }
          break;
      }
    };

    if (isPdfFile) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isPdfFile, numPages]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        if (isExcelFile) {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);

          const arrayBuffer = await response.arrayBuffer();
          const data = new Uint8Array(arrayBuffer);

          try {
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            setExcelData(jsonData);
          } catch (error) {
            console.error("Error parsing Excel file:", error);
            throw new Error("Cannot parse Excel file");
          }
        } else if (!isPdfFile && !isWordFile) {
          // Only fetch content for text files
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);

          const text = await response.text();
          setContent(text);
        }
      } catch (err) {
        console.error("Error fetching file content:", err);
        setError("Failed to load file");
      } finally {
        if (!isPdfFile) {
          setLoading(false);
        }
      }
    };

    if (!isPdfFile && !isWordFile) {
      fetchContent();
    } else if (!isPdfFile) {
      setLoading(false);
    }
  }, [fileUrl, fileExtension, isPdfFile, isExcelFile, isWordFile]);
  
  useEffect(() => {
    if (!content || !searchTerm.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      return;
    }
    
    const results: number[] = [];
    const text = content.toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();
    let index = 0;
    
    while (index < text.length) {
      index = text.indexOf(searchTermLower, index);
      if (index === -1) break;
      results.push(index);
      index += searchTermLower.length;
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);
  }, [content, searchTerm]);
  
  const handleSearchNext = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
  };
  
  const handleSearchPrevious = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
  };
  
  // Scroll to the current search result
  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults.length > 0 && contentRef.current) {
      const currentResult = searchResults[currentSearchIndex];
      const textUptoCurrent = content?.substring(0, currentResult) || '';
      
      const lines = textUptoCurrent.split('\n');
      const lineIndex = lines.length - 1;
      
      const lineElement = contentRef.current.querySelector(`[data-line-number="${lineIndex + 1}"]`);
      if (lineElement) {
        lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentSearchIndex, searchResults, content]);

  // Google Docs Viewer URL for Word documents
  const createWordViewerUrl = () => {
    const encodedFileUrl = encodeURIComponent(fileUrl);
    return `https://docs.google.com/gview?url=${encodedFileUrl}&embedded=true`;
  };

  // Microsoft Office Online Viewer URL for Excel
  const createExcelViewerUrl = () => {
    const encodedFileUrl = encodeURIComponent(fileUrl);
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodedFileUrl}`;
  };

  // Fallback Excel Table Render
  const renderExcelTable = () => {
    if (!excelData || excelData.length === 0) return <p>No data found in the Excel file.</p>;

    const headers = Object.keys(excelData[0]);

    return (
      <div className="overflow-auto max-h-[calc(80vh-10rem)]">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-4 py-2 text-left text-sm font-semibold border">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {excelData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 border">
                    {row[header] !== undefined ? String(row[header]) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Render text content with search highlighting
  const renderTextContent = () => {
    if (!content) return null;
    
    const lines = content.split('\n');
    
    return (
      <div ref={contentRef} className="text-sm font-mono p-4">
        <div className="overflow-x-auto">
          {lines.map((line, lineIndex) => {
            if (searchTerm.trim() === '') {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const lineSearchTerm = searchTerm.toLowerCase();
            const lineLower = line.toLowerCase();
            const matches: { index: number, length: number }[] = [];
            let searchIndex = 0;
            
            while (searchIndex < line.length) {
              const foundIndex = lineLower.indexOf(lineSearchTerm, searchIndex);
              if (foundIndex === -1) break;
              matches.push({ index: foundIndex, length: searchTerm.length });
              searchIndex = foundIndex + lineSearchTerm.length;
            }
            
            if (matches.length === 0) {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const parts: JSX.Element[] = [];
            let lastIndex = 0;
            
            const lineStartIndex = content.split('\n').slice(0, lineIndex).join('\n').length + (lineIndex > 0 ? 1 : 0);
            
            matches.forEach((match, matchIndex) => {
              const absoluteMatchIndex = lineStartIndex + match.index;
              
              if (match.index > lastIndex) {
                parts.push(
                  <span key={`before-${lineIndex}-${matchIndex}`}>
                    {line.substring(lastIndex, match.index)}
                  </span>
                );
              }
              
              const isCurrentMatch = searchResults[currentSearchIndex] === absoluteMatchIndex;
              parts.push(
                <span 
                  key={`highlight-${lineIndex}-${matchIndex}`} 
                  className={isCurrentMatch ? 
                    "bg-yellow-400 text-black" : 
                    "bg-yellow-200 text-black"}
                  id={isCurrentMatch ? "current-search-result" : undefined}
                >
                  {line.substring(match.index, match.index + match.length)}
                </span>
              );
              
              lastIndex = match.index + match.length;
            });
            
            if (lastIndex < line.length) {
              parts.push(
                <span key={`after-${lineIndex}`}>
                  {line.substring(lastIndex)}
                </span>
              );
            }
            
            return (
              <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                {parts.length > 0 ? parts : ' '}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render PDF Viewer
  const renderPdfViewer = () => {
    return (
      <div className="h-full flex flex-col bg-gray-100">
        {/* PDF Controls */}
        <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200 flex-wrap gap-2">
          {/* Page Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page (← or ↑)"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                className="w-16 bg-white text-gray-900 text-center border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <span className="text-sm text-gray-600">of {numPages}</span>
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page (→ or ↓)"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom out (Ctrl + -)"
            >
              <ZoomOut size={16} />
            </button>
            
            <span className="text-sm text-gray-600 min-w-[4rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            
            <button
              onClick={zoomIn}
              disabled={scale >= 3.0}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom in (Ctrl + +)"
            >
              <ZoomIn size={16} />
            </button>
            
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
              title="Reset zoom (Ctrl + 0)"
            >
             Reset
            </button>
          </div>
        </div>

        {/* PDF Document */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          <div className="flex justify-center">
            <div className="bg-white shadow-lg">
              <Document
                file={fileUrl}
                onLoadStart={onDocumentLoadStart}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={pdfOptions}
                loading={
                  <div className="flex flex-col justify-center items-center p-8 min-h-[400px]">
                    <RotateCw className="animate-spin h-8 w-8 text-violet-500 mb-2" />
                    <span className="text-sm text-gray-600">Loading PDF...</span>
                    <span className="text-xs text-gray-500 mt-1">This may take a moment for large files</span>
                  </div>
                }
                error={
                  <div className="text-red-500 p-8 text-center bg-red-50 rounded-md">
                    <p className="font-medium">Failed to load PDF document</p>
                    <p className="text-sm text-gray-600 mt-2">{pdfError}</p>
                    <button
                      onClick={downloadPdf}
                      className="mt-3 px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 text-sm"
                    >
                      Download instead
                    </button>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={onPageLoadError}
                  loading={
                    <div className="flex justify-center items-center p-4 min-h-[400px]">
                      <RotateCw className="animate-spin h-6 w-6 text-violet-500" />
                      <span className="ml-2 text-sm text-gray-600">Loading page...</span>
                    </div>
                  }
                  error={
                    <div className="text-red-500 p-4 text-center">
                      <p>Failed to load page {pageNumber}</p>
                    </div>
                  }
                />
              </Document>
            </div>
          </div>
        </div>

        {/* PDF Keyboard shortcuts info */}
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            Use arrow keys to navigate • Ctrl/Cmd + +/- to zoom • Home/End for first/last page
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-6 text-left align-middle shadow-xl transition-all w-full lg:w-[1110px] sm:w-[90vw] h-[80vh] sm:h-[70vh] lg:h-[650px] overflow-hidden">
        <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
          <h2 className="text-xl font-semibold flex items-center">
            <FileText className="h-5 w-5 mr-2 text-violet-600" />
            {fileName}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={onClose}
              className="rounded-full bg-transparent hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 p-2"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="h-[calc(100%-4rem)] overflow-hidden">
          {loading && !isPdfFile ? (
            <div className="flex justify-center items-center h-full">
              <RotateCw className="animate-spin h-8 w-8 text-violet-500" />
            </div>
          ) : error ? (
            <div className="text-red-500 p-4 bg-red-50 rounded-md">
              <p>Failed to load file: {error}</p>
              <a href={fileUrl} download={fileName} className="text-violet-600 underline">
                Download instead
              </a>
            </div>
          ) : isPdfFile ? (
            renderPdfViewer()
          ) : isWordFile ? (
            <div className="h-full rounded-xl bg-white shadow-sm">
              <iframe
                src={createWordViewerUrl()}
                className="w-full h-full border-0"
                title="Word Document Viewer"
                allowFullScreen={true}
              />
            </div>
          ) : isExcelFile ? (
            <div className="h-full rounded-xl bg-white shadow-sm">
              <iframe
                src={createExcelViewerUrl()}
                className="w-full h-full border-0"
                title="Excel Viewer"
                allowFullScreen={true}
              />
            </div>
          ) : (
            <div className="h-full rounded-xl bg-white shadow-sm flex flex-col">
              <div className="flex items-center p-2 bg-gray-100 border-b border-gray-200">
                <div className="relative flex-grow">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                    <Search size={16} className="text-gray-500" />
                  </span>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search in text..."
                    className="w-full py-1 pl-8 pr-3 bg-white text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
                  />
                </div>
                <div className="flex items-center ml-2">
                  <span className="text-sm text-gray-600 mr-2">
                    {searchResults.length > 0 ? 
                      `${currentSearchIndex + 1} of ${searchResults.length}` : 
                      "No results"}
                  </span>
                  <button 
                    onClick={handleSearchPrevious}
                    disabled={searchResults.length === 0}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Previous match"
                  >
                    <MoveUp size={16} className="text-gray-700" />
                  </button>
                  <button 
                    onClick={handleSearchNext}
                    disabled={searchResults.length === 0}
                    className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Next match"
                  >
                    <MoveDown size={16} className="text-gray-700" />
                  </button>
                </div>
              </div>
              
              <div className="flex-grow overflow-auto">
                {renderTextContent()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;